import * as React from "react";
import { Header } from "@/components/Header";
import { Plus, Edit, Copy, Trash2, Check, RefreshCw, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { DateFilter } from "@/components/DateFilter";
import { useTransactionFilters } from "@/hooks/transactions/useTransactionFilters";
import { useTransactionSelection } from "@/hooks/transactions/useTransactionSelection";
import { useTransactionBulkActions } from "@/hooks/transactions/useTransactionBulkActions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useFinance, Transaction } from "@/context/FinanceContext";
import { TransactionForm } from "@/components/TransactionForm";
import { useToast } from "@/components/ui/use-toast";
import { TransferForm } from "@/components/TransferForm";
import { TransferEditForm } from "@/components/TransferEditForm";
import { BulkEditDialog } from "@/components/BulkEditDialog";
import { BulkPaymentDialog } from "@/components/BulkPaymentDialog";
import { OptimizedExcelTable } from "@/components/OptimizedExcelTable";
import { ActiveFilters } from "@/components/ActiveFilters";
import { useDebounce } from "@/hooks/useDebounce";
import { ExportButtons } from "@/components/ExportButtons";
import { ImportTransactionsButton } from "@/components/ImportTransactionsButton";
import { AttachmentsDialog } from "@/components/AttachmentsDialog";
import { useTransactionAttachments } from "@/hooks/finance/useTransactionAttachments";
import { usePermissions } from "@/hooks/usePermissions";
export default function CleanTransactions() {
  const {
    transactions,
    categories,
    accounts,
    paymentMethods,
    costCenters,
    suppliers,
    removeTransaction,
    updateTransaction,
    toggleTransactionSettled,
    forceRefresh
  } = useFinance();
  const {
    toast
  } = useToast();
  const [searchParams] = useSearchParams();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [filteredTransactions, setFilteredTransactions] = React.useState<Transaction[]>(transactions);

  // Dialog states
  const [isReceitaOpen, setIsReceitaOpen] = React.useState(false);
  const [isDespesaOpen, setIsDespesaOpen] = React.useState(false);
  const [isTransferenciaOpen, setIsTransferenciaOpen] = React.useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = React.useState(false);
  const [showBulkPayment, setShowBulkPayment] = React.useState(false);
  const [editTx, setEditTx] = React.useState<Transaction | null>(null);
  const [editBlockedTx, setEditBlockedTx] = React.useState<Transaction | null>(null);
  const [duplicateTx, setDuplicateTx] = React.useState<Transaction | null>(null);
  const [deleteConfirmTx, setDeleteConfirmTx] = React.useState<Transaction | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = React.useState<{
    count: number;
    transactions: Transaction[];
  } | null>(null);
  const [revertConfirmTx, setRevertConfirmTx] = React.useState<Transaction | null>(null);
  const [attachmentsTx, setAttachmentsTx] = React.useState<Transaction | null>(null);
  const attachmentsApi = useTransactionAttachments();
  const { canAccess } = usePermissions();
  const canEditTransactions = canAccess('transactions', 'edit');

  // Custom hooks for filters and selection
  const {
    columnFilters,
    setColumnFilters,
    handleColumnFilterChange,
    handleClearAllFilters,
    handleRemoveColumnFilter,
    handleDateRangeChange,
    filterByTransactionIds
  } = useTransactionFilters();
  const debouncedColumnFilters = useDebounce(columnFilters, 150);
  const {
    selected,
    setSelected,
    clearSelection
  } = useTransactionSelection();
  const {
    validateBulkDelete,
    executeBulkDelete,
    canDelete
  } = useTransactionBulkActions({
    updateTransaction,
    removeTransaction,
    transactions
  });
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await forceRefresh({ force: true });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar os dados. Tente novamente."
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  React.useEffect(() => {
    document.title = "Lançamentos | Controle Financeiro Pessoal";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Gerencie todos os seus lançamentos financeiros.');
  }, []);

  // Process URL parameters to apply filters automatically
  React.useEffect(() => {
    const urlFilters: Record<string, string[]> = {};

    // Process specific transaction IDs filter (highest priority)
    const transactionIds = searchParams.get('transactionIds');
    if (transactionIds) {
      urlFilters.transactionIds = transactionIds.split(',');
    } else {
      // Only process other filters if transactionIds is not present

      // Process transaction type filter
      const type = searchParams.get('type');
      if (type && (type === 'receita' || type === 'despesa')) {
        urlFilters.type = [type];
      }

      // Process settled filter
      const settled = searchParams.get('settled');
      if (settled === 'false') {
        urlFilters.settled = ['false'];
      }

      // Process overdue filter
      const overdue = searchParams.get('overdue');
      if (overdue === 'true') {
        urlFilters.overdue = ['true'];
      }

      // Process due date filter
      const dueDate = searchParams.get('dueDate');
      if (dueDate) {
        // If it's today's date, use today filter
        const today = format(new Date(), 'yyyy-MM-dd');
        if (dueDate === today) {
          urlFilters.dueDate = [dueDate];
        } else {
          urlFilters.dueDate = [dueDate];
        }
      }

      // Filtro por intervalo de Data de Pagamento (vindo de Saldos Bancários)
      const paymentStart = searchParams.get('paymentDateStart');
      const paymentEnd = searchParams.get('paymentDateEnd');
      if (paymentStart || paymentEnd) {
        urlFilters.paymentDate = [JSON.stringify({
          type: 'dateRange',
          start: paymentStart || undefined,
          end: paymentEnd || undefined,
        })];
        urlFilters.status = ['Pago'];
      }

      // Filtro por conta (vindo de Saldos Bancários) — usa nome da conta
      const accountName = searchParams.get('accountName');
      if (accountName) {
        urlFilters.account = [accountName];
      }
    }

    // Apply URL filters if any exist
    if (Object.keys(urlFilters).length > 0) {
      setColumnFilters(prevFilters => ({
        ...prevFilters,
        ...urlFilters
      }));
    }
  }, [searchParams]);

  // Filter transactions based on transactionIds if present in URL
  const companyFilteredTransactions = React.useMemo(() => {
    return filterByTransactionIds(transactions, columnFilters.transactionIds);
  }, [transactions, columnFilters.transactionIds, filterByTransactionIds]);
  const selectedTransactions = React.useMemo(
    () => companyFilteredTransactions.filter(transaction => selected.has(transaction.id)),
    [companyFilteredTransactions, selected]
  );
  const selectedBulkType = React.useMemo(() => {
    const types = new Set(selectedTransactions.map(transaction => transaction.type));
    return types.size === 1 ? selectedTransactions[0]?.type : undefined;
  }, [selectedTransactions]);
  const bulkEditCategories = React.useMemo(
    () => selectedBulkType ? categories.filter(category => category.type === selectedBulkType) : [],
    [categories, selectedBulkType]
  );
  const handleOpenBulkEdit = React.useCallback(() => {
    if (!selectedBulkType) {
      toast({
        variant: "destructive",
        title: "Edição em lote bloqueada",
        description: "Selecione apenas receitas ou apenas despesas para editar em lote.",
        duration: 5000
      });
      return;
    }

    const hasReconciledTransactions = selectedTransactions.some(transaction => transaction.reconciled === true);
    if (hasReconciledTransactions) {
      toast({
        variant: "destructive",
        title: "Edição bloqueada",
        description: "Há lançamentos conciliados na seleção. Desfaça a conciliação antes de editar.",
        duration: 5000
      });
      return;
    }

    setIsBulkEditOpen(true);
  }, [selectedBulkType, selectedTransactions, toast]);
  const handleDuplicateTransaction = (tx: Transaction) => {
    setDuplicateTx(tx);
  };
  const handleDeleteTransaction = (tx: Transaction) => {
    if (!canDelete(tx)) {
      setDeleteConfirmTx(null);
      return;
    }
    removeTransaction(tx.id);
    setDeleteConfirmTx(null);
  };
  const handleRevertTransaction = async (id: string) => {
    const transaction = companyFilteredTransactions.find(t => t.id === id);
    if (transaction) {
      if (transaction.reconciled === true) {
        toast({
          variant: "destructive",
          title: "Lançamento conciliado",
          description: "Este lançamento está conciliado. Remova a conciliação na tela de Conciliação OFX antes de estornar.",
          duration: 5000,
        });
        return;
      }
      setRevertConfirmTx(transaction);
    }
  };
  const confirmRevertTransaction = async () => {
    if (!revertConfirmTx) return;

    // Fechar dialog PRIMEIRO
    setRevertConfirmTx(null);
    try {
      await toggleTransactionSettled(revertConfirmTx.id);

      // Toast DEPOIS que o dialog já fechou
      toast({
        title: "Pagamento estornado",
        description: "O lançamento foi marcado como não pago."
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao estornar",
        description: "Não foi possível estornar o pagamento."
      });
    }
  };
  const handleEdit = (transaction: Transaction) => {
    // Bloquear edição apenas se conciliado (não apenas pago)
    if (transaction.reconciled === true) {
      setEditBlockedTx(transaction);
      return;
    }
    setEditTx(transaction);
  };
  const handleDelete = (transaction: Transaction) => {
    if (!canDelete(transaction)) {
      return;
    }
    setDeleteConfirmTx(transaction);
  };
  return <div className="min-h-screen bg-background">
      <Header />

      <main className="page-bleed py-4 md:py-8 space-y-4 md:space-y-6">
        {/* Header with Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Lançamentos</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">Gerencie suas receitas e despesas</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            
            <ExportButtons transactions={filteredTransactions} categories={categories} accounts={accounts} companies={[]} paymentMethods={paymentMethods} costCenters={costCenters} suppliers={suppliers} />
            <ImportTransactionsButton />
          </div>
        </div>

        {/* Action Cards - Redesigned */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Nova Receita - Enhanced */}
          <Dialog open={isReceitaOpen} onOpenChange={setIsReceitaOpen}>
            <DialogTrigger asChild>
              <Card className="group cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] border-success/30 bg-gradient-to-br from-success/5 to-success/10 hover:from-success/10 hover:to-success/20">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center md:items-start gap-3 md:gap-4">
                    <div className="rounded-xl bg-success/20 p-2 md:p-3 group-hover:bg-success/30 transition-colors">
                      <Plus className="h-5 w-5 md:h-6 md:w-6 text-success" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base md:text-lg text-foreground">Nova Receita</h3>
                      <p className="text-xs md:text-sm text-muted-foreground hidden md:block">Adicionar entrada de recursos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent size="huge" className="max-h-[90vh] overflow-y-auto" onPointerDownOutside={e => e.preventDefault()}>
              <DialogHeader className="bg-success text-success-foreground p-6 -m-6 mb-6 rounded-t-lg">
                <DialogTitle className="text-white text-xl">Nova Receita</DialogTitle>
              </DialogHeader>
              <TransactionForm defaultValues={{
              type: "receita"
            }} lockType="receita" onSubmitted={() => setIsReceitaOpen(false)} />
            </DialogContent>
          </Dialog>

          {/* Nova Despesa - Enhanced */}
          <Dialog open={isDespesaOpen} onOpenChange={setIsDespesaOpen}>
            <DialogTrigger asChild>
              <Card className="group cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] border-destructive/30 bg-gradient-to-br from-destructive/5 to-destructive/10 hover:from-destructive/10 hover:to-destructive/20">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center md:items-start gap-3 md:gap-4">
                    <div className="rounded-xl bg-destructive/20 p-2 md:p-3 group-hover:bg-destructive/30 transition-colors">
                      <Plus className="h-5 w-5 md:h-6 md:w-6 text-destructive" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base md:text-lg text-foreground">Nova Despesa</h3>
                      <p className="text-xs md:text-sm text-muted-foreground hidden md:block">Registrar saída de recursos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent size="huge" className="max-h-[90vh] overflow-y-auto" onPointerDownOutside={e => e.preventDefault()}>
              <DialogHeader className="bg-destructive text-destructive-foreground p-6 -m-6 mb-6 rounded-t-lg">
                <DialogTitle className="text-white text-xl">Nova Despesa</DialogTitle>
              </DialogHeader>
              <TransactionForm defaultValues={{
              type: "despesa"
            }} lockType="despesa" onSubmitted={() => setIsDespesaOpen(false)} />
            </DialogContent>
          </Dialog>

          {/* Nova Transferência - Enhanced */}
          <Dialog open={isTransferenciaOpen} onOpenChange={setIsTransferenciaOpen}>
            <DialogTrigger asChild>
              <Card className="group cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] border-warning/30 bg-gradient-to-br from-warning/5 to-warning/10 hover:from-warning/10 hover:to-warning/20">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center md:items-start gap-3 md:gap-4">
                    <div className="rounded-xl bg-warning/20 p-2 md:p-3 group-hover:bg-warning/30 transition-colors">
                      <Plus className="h-5 w-5 md:h-6 md:w-6 text-warning" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base md:text-lg text-foreground">Nova Transferência</h3>
                      <p className="text-xs md:text-sm text-muted-foreground hidden md:block">Mover entre contas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </DialogTrigger>
            <DialogContent size="huge" onPointerDownOutside={e => e.preventDefault()}>
              <DialogHeader className="bg-warning text-warning-foreground p-4 -m-6 mb-6 rounded-t-lg">
                <DialogTitle>Nova Transferência</DialogTitle>
              </DialogHeader>
              <TransferForm onSubmitted={() => setIsTransferenciaOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Date Filter */}
        <DateFilter onDateRangeChange={handleDateRangeChange} currentFilters={columnFilters} />


        {/* Selected Actions - Redesigned */}
        {selected.size > 0 && <Card className="border-primary/50 bg-gradient-to-r from-primary/5 to-primary/10 shadow-md">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/20 p-2">
                    <Check className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      {selected.size} {selected.size === 1 ? 'lançamento selecionado' : 'lançamentos selecionados'}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-muted-foreground">Escolha uma ação abaixo</p>
                      <Button variant="outline" size="sm" onClick={clearSelection} className="h-6 gap-1 px-2 text-xs border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
                        <X className="h-3 w-3" />
                        Limpar seleção
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:ml-auto">
                  {/* Editar ou Editar em Lote */}
                  {selected.size === 1 ? <Button variant="outline" size="sm" onClick={() => {
                const transaction = companyFilteredTransactions.find(t => selected.has(t.id));
                if (transaction) handleEdit(transaction);
              }} className="gap-2">
                      <Edit className="h-4 w-4" />
                      Editar
                    </Button> : <Button variant="outline" size="sm" onClick={handleOpenBulkEdit} className="gap-2">
                      <Edit className="h-4 w-4" />
                      Editar em Lote
                    </Button>}

                  {/* Confirmar/Estornar Pagamento */}
                  {(() => {
                const selectedTransactions = companyFilteredTransactions.filter(t => selected.has(t.id));
                const hasSettledTransactions = selectedTransactions.some(t => t.settled);
                const hasReconciled = selectedTransactions.some(t => t.reconciled === true);
                return <Button variant="outline" size="sm" onClick={() => {
                  if (hasSettledTransactions && hasReconciled) {
                    toast({
                      variant: "destructive",
                      title: "Estorno bloqueado",
                      description: "Há lançamentos conciliados na seleção. Remova a conciliação na tela de Conciliação OFX antes de estornar.",
                      duration: 5000,
                    });
                    return;
                  }
                  setShowBulkPayment(true);
                }} className="gap-2">
                        <Check className="h-4 w-4" />
                        {hasSettledTransactions ? "Estornar" : "Confirmar"}
                      </Button>;
              })()}

                  {/* Duplicar - só aparece quando 1 item selecionado */}
                  {selected.size === 1 && <Button variant="outline" size="sm" onClick={() => {
                const transaction = companyFilteredTransactions.find(t => selected.has(t.id));
                if (transaction) handleDuplicateTransaction(transaction);
              }} className="gap-2">
                      <Copy className="h-4 w-4" />
                      Duplicar
                    </Button>}
                   
                   {/* Excluir */}
                   <Button variant="destructive" size="sm" onClick={() => {
                const selectedTransactions = companyFilteredTransactions.filter(t => selected.has(t.id));

                // Apenas validar (não deletar ainda)
                const validation = validateBulkDelete(selected);
                if (validation.canDelete) {
                  // Mostrar dialog de confirmação
                  setBulkDeleteConfirm({
                    count: selected.size,
                    transactions: selectedTransactions
                  });
                }
                // Se não pode deletar, o toast de erro já foi mostrado
              }} className="gap-2">
                    <Trash2 className="h-4 w-4" />
                    Excluir
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>}

        {/* Optimized Excel Table with Virtualization */}
        <OptimizedExcelTable transactions={companyFilteredTransactions} categories={categories} accounts={accounts} companies={[]} paymentMethods={paymentMethods} costCenters={costCenters} suppliers={suppliers} selected={selected} onSelectionChange={setSelected} onEdit={handleEdit} onDelete={handleDelete} onDuplicate={handleDuplicateTransaction} onRevert={handleRevertTransaction} onBulkEdit={handleOpenBulkEdit} onBulkDelete={() => setDeleteConfirmTx({
        id: 'bulk'
      } as Transaction)} onConfirmPayment={() => setShowBulkPayment(true)} columnFilters={debouncedColumnFilters} onColumnFilterChange={handleColumnFilterChange} onClearAllFilters={handleClearAllFilters} onFilteredTransactionsChange={setFilteredTransactions} attachmentCounts={attachmentsApi.counts} onOpenAttachments={setAttachmentsTx} />

        <AttachmentsDialog
          open={!!attachmentsTx}
          onOpenChange={(open) => !open && setAttachmentsTx(null)}
          transaction={attachmentsTx}
          attachmentsApi={attachmentsApi}
          canEdit={canEditTransactions}
        />

        {/* Dialogs */}
        <Dialog open={!!editTx} onOpenChange={() => setEditTx(null)}>
          <DialogContent size="full" className="max-h-[95vh] overflow-y-auto">
            <DialogHeader className="bg-primary text-primary-foreground p-6 -m-6 mb-6 rounded-t-lg">
              <DialogTitle className="text-white text-xl">Editar Lançamento</DialogTitle>
            </DialogHeader>
            {editTx && editTx.kind === "transferencia" ? <TransferEditForm groupId={editTx.transferGroupId || editTx.id} onSubmitted={() => setEditTx(null)} /> : editTx && <TransactionForm transactionId={editTx.id} defaultValues={{
            description: editTx.description,
            value: String(editTx.value),
            type: editTx.type,
            categoryId: editTx.categoryId,
            accountId: editTx.accountId,
            costCenterId: editTx.costCenterId || "",
            paymentMethodId: editTx.paymentMethodId || "",
            supplierId: editTx.supplierId || "",
            issueDate: editTx.issueDate || "",
            dueDate: editTx.dueDate || "",
            paymentDate: editTx.paymentDate || "",
            parcelMode: editTx.parcelMode || "total",
            parcels: editTx.parcels,
            issueDateMode: editTx.issueDateMode || "fixed",
            settled: editTx.settled ?? true
          }} onSubmitted={() => setEditTx(null)} />}
          </DialogContent>
        </Dialog>

        <Dialog open={!!duplicateTx} onOpenChange={() => setDuplicateTx(null)}>
          <DialogContent size="full" className="max-h-[95vh] overflow-y-auto">
            <DialogHeader className="bg-secondary text-secondary-foreground p-6 -m-6 mb-6 rounded-t-lg">
              <DialogTitle className="text-white text-xl">Duplicar Lançamento</DialogTitle>
            </DialogHeader>
            {duplicateTx && <TransactionForm lockType={duplicateTx.type} defaultValues={{
            description: duplicateTx.description,
            value: String(duplicateTx.value),
            type: duplicateTx.type,
            categoryId: duplicateTx.categoryId,
            accountId: duplicateTx.accountId,
            costCenterId: duplicateTx.costCenterId || "",
            paymentMethodId: duplicateTx.paymentMethodId || "",
            issueDate: duplicateTx.issueDate,
            dueDate: duplicateTx.dueDate,
            parcelMode: duplicateTx.parcelMode,
            parcels: duplicateTx.parcels
          }} onSubmitted={() => setDuplicateTx(null)} />}
          </DialogContent>
        </Dialog>

        <BulkEditDialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen} selectedCount={selected.size} transactionType={selectedBulkType} categories={bulkEditCategories} accounts={accounts} companies={[]} costCenters={costCenters} paymentMethods={paymentMethods} suppliers={suppliers} onApply={data => {
        selected.forEach(id => {
          updateTransaction(id, data);
        });
        clearSelection();
        setIsBulkEditOpen(false);
        toast({
          title: "Edição em lote aplicada",
          description: `${selected.size} lançamentos foram atualizados.`,
          duration: 1000
        });
      }} />

        <BulkPaymentDialog open={showBulkPayment} onOpenChange={setShowBulkPayment} selectedIds={Array.from(selected)} onSubmit={() => {
        setShowBulkPayment(false);
        setTimeout(() => {
          clearSelection();
        }, 0);
      }} />

        {deleteConfirmTx && <AlertDialog open={!!deleteConfirmTx} onOpenChange={() => setDeleteConfirmTx(null)}>
            <AlertDialogContent className="p-0">
              <AlertDialogHeader className="bg-destructive text-destructive-foreground p-6 rounded-t-lg">
                <AlertDialogTitle className="text-white text-xl">
                  {deleteConfirmTx.reconciled ? "⚠️ Exclusão Bloqueada" : "Confirmar Exclusão"}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <div className="p-6">
                <AlertDialogDescription className="text-base">
                  {deleteConfirmTx.reconciled ? <span className="text-destructive font-semibold">
                      Este lançamento está CONCILIADO e não pode ser excluído. Desfaça a conciliação antes de excluir.
                    </span> : <>
                      Tem certeza que deseja excluir o lançamento <strong>"{deleteConfirmTx.description}"</strong>? Esta ação não pode ser desfeita.
                    </>}
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="px-6 pb-6">
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                {!deleteConfirmTx.reconciled && <AlertDialogAction onClick={() => handleDeleteTransaction(deleteConfirmTx)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Excluir
                  </AlertDialogAction>}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}

        {/* Dialog de Exclusão em Lote */}
        {bulkDeleteConfirm && <AlertDialog open={!!bulkDeleteConfirm} onOpenChange={open => {
        if (!open) setBulkDeleteConfirm(null);
      }}>
            <AlertDialogContent className="p-0">
              <AlertDialogHeader className="bg-destructive text-destructive-foreground p-6 rounded-t-lg">
                <AlertDialogTitle className="text-white text-xl">
                  Confirmar Exclusão em Lote
                </AlertDialogTitle>
              </AlertDialogHeader>
              <div className="p-6">
                <AlertDialogDescription className="text-base">
                  Tem certeza que deseja excluir <strong>{bulkDeleteConfirm.count} lançamentos</strong>? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="px-6 pb-6">
                <AlertDialogCancel onClick={() => setBulkDeleteConfirm(null)}>
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction onClick={async () => {
              // Executar exclusão AQUI (após confirmação)
              await executeBulkDelete(new Set(bulkDeleteConfirm.transactions.map(t => t.id)), () => {
                clearSelection();
                setBulkDeleteConfirm(null);
              });
            }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Excluir {bulkDeleteConfirm.count} Lançamentos
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}

        {/* Dialog de Confirmação de Estorno */}
        {revertConfirmTx && <AlertDialog open={!!revertConfirmTx} onOpenChange={open => {
        if (!open) setRevertConfirmTx(null);
      }}>
            <AlertDialogContent className="p-0">
              <AlertDialogHeader className="bg-warning text-warning-foreground p-6 rounded-t-lg">
                <AlertDialogTitle className="text-xl">
                  Confirmar Estorno de Pagamento
                </AlertDialogTitle>
              </AlertDialogHeader>
              <div className="p-6">
                <AlertDialogDescription className="text-base space-y-2">
                  <p>Tem certeza que deseja estornar o pagamento deste lançamento?</p>
                  <div className="mt-4 p-3 bg-muted rounded-md">
                    <p className="text-sm font-medium">
                      <strong>Descrição:</strong> {revertConfirmTx.description}
                    </p>
                    <p className="text-sm">
                      <strong>Valor:</strong> {revertConfirmTx.value.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    O lançamento será marcado como não pago e a data de pagamento será removida.
                  </p>
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="px-6 pb-6">
                <AlertDialogCancel onClick={() => setRevertConfirmTx(null)}>
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction onClick={confirmRevertTransaction} className="bg-warning text-warning-foreground hover:bg-warning/90">
                  Estornar Pagamento
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}

        {/* Dialog de Edição Bloqueada */}
        {editBlockedTx && <AlertDialog open={!!editBlockedTx} onOpenChange={() => setEditBlockedTx(null)}>
            <AlertDialogContent className="p-0">
              <AlertDialogHeader className="bg-warning text-warning-foreground p-6 rounded-t-lg">
                <AlertDialogTitle className="text-xl">
                  ⚠️ Edição Bloqueada
                </AlertDialogTitle>
              </AlertDialogHeader>
              <div className="p-6">
                <AlertDialogDescription className="text-base space-y-2">
                  <span className="text-warning font-semibold block">
                    Este lançamento está CONCILIADO e não pode ser editado.
                  </span>
                  <p className="text-muted-foreground">
                    Para editar este lançamento, desfaça a conciliação bancária na aba de Conciliação.
                  </p>
                  <div className="mt-4 p-3 bg-muted rounded-md">
                    <p className="text-sm font-medium">
                      <strong>Descrição:</strong> {editBlockedTx.description}
                    </p>
                    <p className="text-sm">
                      <strong>Valor:</strong> {editBlockedTx.value.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                    </p>
                  </div>
                </AlertDialogDescription>
              </div>
              <AlertDialogFooter className="px-6 pb-6">
                <AlertDialogCancel>Entendi</AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
        
      </main>
    </div>;
}